import { toWSDateString } from "@wingspanhq/fe-component-library";
import {
  RecurringDocumentStatus,
  RecurringInvoice
} from "@wingspanhq/wingspan-api-sdk";
import { selectorIsRecurringSeries } from "../../shared/selectors/selectorIsRecurringSeries";
import { getLocalStartDayByUTC } from "../../utils/dates";

export type RecurringInvoiceHeaderState = {
  kind: "activeSeries" | "activeOneOff" | "draft" | "readOnly";
  title: string;
  description: string;
};

export const selectorRecurringInvoiceHeaderState = (
  recurringInvoice: RecurringInvoice
): RecurringInvoiceHeaderState => {
  const isSeries = selectorIsRecurringSeries(recurringInvoice);
  const noun = isSeries ? "Series" : "Invoice";
  const firstSendDate =
    recurringInvoice.schedule?.scheduleDates?.[0]?.date ??
    recurringInvoice.nextOccurrenceDate ??
    recurringInvoice.schedule?.anchorDate;

  switch (recurringInvoice.status) {
    case RecurringDocumentStatus.ACTIVE: {
      if (!isSeries) {
        return {
          kind: "activeOneOff",
          title: "Invoice is scheduled",
          description: firstSendDate
            ? `Send date is ${toWSDateString(
                getLocalStartDayByUTC(firstSendDate),
                "monthDayYear"
              )}`
            : "Send date is not set"
        };
      }

      const hasGeneratedAnOccurrence = !!recurringInvoice.lastOccurrenceDate;
      const nextSendDate = recurringInvoice.nextOccurrenceDate ?? firstSendDate;

      return {
        kind: "activeSeries",
        title: "Invoice is scheduled",
        description: nextSendDate
          ? `${
              hasGeneratedAnOccurrence ? "Next" : "First"
            } invoice will be sent ${toWSDateString(
              getLocalStartDayByUTC(nextSendDate),
              "monthDayYear"
            )}`
          : "The next send date is not set"
      };
    }

    case RecurringDocumentStatus.DRAFT:
      return {
        kind: "draft",
        title: "This is a draft",
        description: "Send now or schedule for later"
      };

    case RecurringDocumentStatus.PAUSED:
      return {
        kind: "readOnly",
        title: `${noun} is paused`,
        description: `${prefixDate(
          "Paused",
          recurringInvoice.events?.pausedAt
        )}No invoices will be sent until it's resumed.`
      };

    case RecurringDocumentStatus.COMPLETED:
      return isSeries
        ? {
            kind: "readOnly",
            title: "Series is complete",
            description: `${prefixDate(
              "The last invoice was sent",
              recurringInvoice.lastOccurrenceDate
            )}This series has finished and won't send again.`
          }
        : {
            kind: "readOnly",
            title: "Invoice was sent",
            description: `${prefixDate(
              "Sent",
              recurringInvoice.lastOccurrenceDate
            )}This scheduled invoice won't send again.`
          };

    case RecurringDocumentStatus.CANCELLED:
      return {
        kind: "readOnly",
        title: `${noun} was cancelled`,
        description: `${prefixDate(
          "Cancelled",
          recurringInvoice.events?.cancelledAt
        )}No further invoices will be sent.`
      };

    default:
      return {
        kind: "readOnly",
        title: `${noun} isn't active`,
        description: `No actions are available for this ${noun.toLowerCase()}.`
      };
  }
};

const prefixDate = (
  label: string,
  date?: Date | null,
  isDateOnly = false
): string =>
  date
    ? `${label} ${toWSDateString(
        isDateOnly ? getLocalStartDayByUTC(date) : date,
        "monthDayYear"
      )}. `
    : "";
